import type { SituationComparator } from '@policyvision/contracts';
import { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/common/components/ui/table';
import { ScrollShadows } from '@/policy/component/ScrollShadows/ScrollShadows';
import { useScroll } from '@/policy/hooks/useScroll';

import { useFetchComparator } from '../hooks/useFetchComparator';
import { ComparatorFooter } from './ComparatorFooter';
import ComparatorHeader from './ComparatorHeader';
import { ComparatorListSkeleton } from './ComparatorListSkeleton';
import ComparatorRow from './ComparatorRow';

interface ComparatorListProps {
  renewalUuid: string;
  contentRef: RefObject<HTMLTableElement | null>;
}

const TOTAL_COLUMNS = 9;

const ComparatorList = ({ renewalUuid, contentRef }: ComparatorListProps) => {
  const { t } = useTranslation();
  const {
    scrollContainerRef,
    dragProps,
    canScrollLeft,
    canScrollRight,
    canScrollDown,
    canScrollUp,
  } = useScroll();

  const { lastYear, currentYear, situations, isLoading } =
    useFetchComparator(renewalUuid);

  if (!contentRef) return;

  if (isLoading) return <ComparatorListSkeleton />;

  if (situations.length === 0)
    return (
      <p className="flex items-center justify-center h-full">
        {t('renewal.noRenewalsFound')}
      </p>
    );

  return (
    <ScrollShadows
      canScrollLeft={canScrollLeft}
      canScrollRight={canScrollRight}
      canScrollDown={canScrollDown}
      canScrollUp={false}
      className="bg-background shadow-sm overflow-hidden flex-1 flex flex-col min-h-0 min-w-0 p-4"
      offsets={{
        top: 80,
        left: 0,
        bottom: 0,
        right: 0,
      }}
    >
      <div
        ref={scrollContainerRef}
        {...dragProps}
        className={`overflow-x-auto flex-1 z-0 ${dragProps.className}
          [&::-webkit-scrollbar]:h-1.5
          [&::-webkit-scrollbar]:w-1.5
          [&::-webkit-scrollbar-track]:bg-transparent
          [&::-webkit-scrollbar-thumb]:bg-warm-gray-400
          [&::-webkit-scrollbar-thumb]:rounded-full
          rounded-t-lg`}
      >
        <Table noWrapper className="w-full min-w-full" ref={contentRef}>
          <ComparatorHeader
            isScrolled={canScrollUp}
            lastYearLabel={
              <span className="flex flex-col p-1 gap-1">
                <p className="font-bold text-xs">
                  {lastYear || t('renewal.comparator.noPreviousPeriod')}
                </p>
              </span>
            }
            currentYearLabel={
              <span className="flex flex-col p-1 gap-1">
                <p className="font-bold text-xs">
                  {currentYear || t('renewal.comparator.noRenewalPeriod')}
                </p>
              </span>
            }
          />

          <TableBody>
            {[
              ...situations.flatMap(
                (situation: SituationComparator, idx: number) => [
                  <TableRow key={`section-${situation.id}`}>
                    <TableCell
                      colSpan={TOTAL_COLUMNS}
                      className={`bg-muted font-semibold border-l border-r border-warm-gray-200 ${idx === 0 ? 'rounded-tl-lg border-t ' : ''}`}
                    >
                      {situation.title}
                    </TableCell>
                  </TableRow>,
                  ...situation.rows.map((row, idx) => (
                    <ComparatorRow
                      key={row.id}
                      row={row}
                      isLast={idx === situation.rows.length - 1}
                    />
                  )),
                ],
              ),
              <ComparatorFooter
                key="comparator-footer"
                warranties={situations.flatMap((situation) => situation.rows)}
              />,
            ]}
          </TableBody>
        </Table>
      </div>
    </ScrollShadows>
  );
};

export default ComparatorList;
